<template>
  <div v-if="allTags.length" class="d-flex flex-wrap ga-1">
    <v-chip
      v-for="tag in allTags"
      :key="tag"
      :size="size"
      :color="tagColor(tag)"
      variant="tonal"
      :prepend-icon="isSystemTag(tag) ? 'mdi-lightning-bolt' : undefined"
    >
      {{ tagLabel(tag) }}
    </v-chip>
  </div>
</template>

<script lang="ts" setup>
const props = withDefaults(
  defineProps<{
    tags?: string[]
    systemTags?: string[]
    size?: 'x-small' | 'small' | 'default'
  }>(),
  { tags: () => [], systemTags: () => [], size: 'x-small' },
)

// ── controlled-vocabulary label maps ─────────────────────────────────────────

const USER_TAG_LABELS: Record<string, string> = {
  HOT: 'Hot',
  FEATURED: 'Featured',
  PRICE_REDUCED: 'Price Reduced',
  NEW_ARRIVAL: 'New Arrival',
  TEST_DRIVE: 'Test Drive',
  DEMO: 'Demo',
  WHOLESALE: 'Wholesale',
  AS_IS: 'As-Is',
  MANAGER_SPECIAL: 'Manager Special',
  CERTIFIED: 'Certified',
}

const SYSTEM_TAG_LABELS: Record<string, string> = {
  FCTP: 'FCTP',
  LOANER: 'Loaner',
  RECENTLY_ADDED: 'Recently Added',
  RECENTLY_SOLD: 'Recently Sold',
  AGED_30_PLUS: 'Aged 30+',
  AGED_60_PLUS: 'Aged 60+',
  PRICE_DROP: 'Price Drop',
  MISSING_PHOTOS: 'Missing Photos',
}

const USER_TAG_COLORS: Record<string, string> = {
  HOT: 'deep-orange',
  FEATURED: 'amber',
  PRICE_REDUCED: 'green',
  NEW_ARRIVAL: 'cyan',
  TEST_DRIVE: 'blue',
  DEMO: 'purple',
  WHOLESALE: 'brown',
  AS_IS: 'grey',
  MANAGER_SPECIAL: 'pink',
  CERTIFIED: 'teal',
}

const SYSTEM_TAG_COLORS: Record<string, string> = {
  FCTP: 'blue',
  LOANER: 'indigo',
  RECENTLY_ADDED: 'light-green',
  RECENTLY_SOLD: 'orange',
  AGED_30_PLUS: 'deep-orange',
  AGED_60_PLUS: 'error',
  PRICE_DROP: 'success',
  MISSING_PHOTOS: 'warning',
}

const systemTagSet = new Set(Object.keys(SYSTEM_TAG_LABELS))

const isSystemTag = (tag: string) => systemTagSet.has(tag)

const tagLabel = (tag: string) =>
  USER_TAG_LABELS[tag] ?? SYSTEM_TAG_LABELS[tag] ?? tag

const tagColor = (tag: string) =>
  isSystemTag(tag) ? (SYSTEM_TAG_COLORS[tag] ?? 'grey') : (USER_TAG_COLORS[tag] ?? 'primary')

const allTags = computed(() => [
  ...(props.tags ?? []),
  ...(props.systemTags ?? []),
])

import { computed } from 'vue'
</script>
